import {compact} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import {Genome} from 'constants/GenomeConstants';
import GenomeImportHelpText from 'molbio/genome/GenomeImportHelpText';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import escapeRegExp from 'util/escapeRegExp';
import {genomeToName} from 'util/GenomeUtil';
import sortedBy from 'util/sortedBy';

interface GenomeSelectorProps {
  disabled?: boolean;
  genomes: Array<Genome>;
  genomeLink: {
    value: string;
    requestChange: (e: string) => void;
  };
}

function GenomeSelector({disabled = false, genomes, genomeLink}: GenomeSelectorProps): JSX.Element {
  const genomesByEnsemblName: Record<string, Genome> = useMemo(
    () =>
      genomes.reduce<Record<string, Genome>>((acc, genome) => {
        acc[genome.ensemblName] = genome;
        return acc;
      }, {}),
    [genomes]
  );
  const sortedGenomes = useMemo(
    () =>
      sortedBy(genomes, [
        {
          accessor: (genome) => genome.ucscName ?? -1,
          reversed: true,
        },
        (genome) => genome.organismName,
      ]),
    [genomes]
  );
  const optionDatas = sortedGenomes.map((genome) => ({
    value: genome.ensemblName,
    name: genomeToName(genome),
    dataTestAttrs: {component: 'GenomeSelector', element: 'option', key: genome.ensemblName},
  }));

  return (
    <div>
      <SelectDropdown
        dataTestAttrs={{component: 'GenomeSelector', element: 'selectRoot'}}
        displayName={genomeToName(genomesByEnsemblName[genomeLink.value])}
        disabled={disabled}
        menu={
          <SearchableMenu
            getMatchingOptionDatas={(query: string, optionDatas: Array<OptionData>) => {
              // Split search query and search for substrings across all genomes names. For example, 'd m'
              // should match 'Drosophilia melanogaster'
              const escaped = escapeRegExp(query);
              const terms = compact(escaped.split(/\s+/));

              // Filter the list of options down to those that match every term.
              return optionDatas.filter((optionData) => {
                const genome = genomesByEnsemblName[optionData.value];
                return terms.every((term) => {
                  const regex = new RegExp(term, 'i');
                  return (
                    regex.test(genome.ucscName || '') ||
                    regex.test(genome.organismName) ||
                    regex.test(genome.ensemblName) ||
                    regex.test(genome.organism != null ? genome.organism.keywords : '')
                  );
                });
              });
            }}
            onSelect={(selectedValue) => {
              genomeLink.requestChange(selectedValue);
            }}
            optionDatas={optionDatas}
            selectedValue={genomeLink.value}
            width={MenuWidth.FULL}
          />
        }
      />
      <GenomeImportHelpText />
    </div>
  );
}

export default GenomeSelector;
